import React, { Children } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';

import {
  practiceSettingsSubtitleSx,
  practiceSettingsTitleSx,
} from '../PracticeSettings/practiceSettingsTypography';

export interface PracticeSectionCardProps {
  heading: string;
  flat?: boolean;
  /** Educational blurb — what this section controls and why it matters. */
  description: string;
  /**
   * Primary management action, shown top-right of the header. Optional — a
   * section whose management surface is embedded in the card (e.g. the
   * appointment-labels manager) has no separate destination to link to.
   */
  manageLabel?: string;
  onManage?: () => void;
  /**
   * Optional overview content rendered in the top subsection, directly below
   * the header (no divider — it reads as part of the same block). E.g. the
   * appointment-type "Current practice setup" signals.
   */
  overview?: React.ReactNode;
  /**
   * Optional browse-style entry rows (`SectionEntryRow`s) rendered below the
   * top subsection, separated from it and from each other by dividers.
   */
  children?: React.ReactNode;
}

/**
 * One practice sub-section, rendered as a single bordered card: an educational
 * header (title + blurb + primary "Manage …" action) and, optionally, a
 * divider-separated stack of browse-style entry rows. The border lives here, at
 * the section level — the entry rows inside are borderless.
 */
export const PracticeSectionCard: React.FC<PracticeSectionCardProps> = ({
  heading,
  flat = false,
  description,
  manageLabel,
  onManage,
  overview,
  children,
}) => {
  const rows = Children.toArray(children).filter(Boolean);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: flat ? 0 : { xs: 2, md: 3 },
        borderRadius: flat ? 0 : 2,
        ...(flat && { border: 0, boxShadow: 'none' }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { sm: 'flex-start' },
          gap: 2,
        }}
      >
        <Box sx={{ maxWidth: 720 }}>
          <Typography
            component={flat ? 'h1' : 'h6'}
            variant="h6"
            sx={flat ? practiceSettingsTitleSx : { fontWeight: 700 }}
          >
            {heading}
          </Typography>
          <Typography variant="body2" sx={practiceSettingsSubtitleSx}>
            {description}
          </Typography>
        </Box>
        {manageLabel && onManage && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onManage}
            sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          >
            {manageLabel}
          </Button>
        )}
      </Box>

      {overview && <Box sx={{ mt: { xs: 2, md: 2.5 } }}>{overview}</Box>}

      {rows.length > 0 && (
        <>
          <Divider sx={{ my: { xs: 2, md: 2.5 } }} />
          <Stack spacing={2.5} divider={<Divider />}>
            {rows}
          </Stack>
        </>
      )}
    </Paper>
  );
};
